<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue';
import { useContentCollection } from '@ilteducation/content-collections';
import config from '@/config/ConfigProvider';
import { useLocale } from '@/plugins/translation';
import { useVideos } from '@ilteducation/videos';
import { useCategories } from '@ilteducation/content-ref-data';
import type { SearchQuery } from '@ilteducation/content-search';
import { storeToRefs } from 'pinia';
import VideoThumbnail from './VideoThumbnail.vue';

const countryCode = config.value('countryCode') as string;
const collectionId = ref(process.env[`VITE_LATEST_VIDEOS_COLLECTION_${countryCode.toUpperCase()}`] || '');
const { collection } = useContentCollection(collectionId);
const videoStore = useVideos();
const { searchResults } = storeToRefs(videoStore);
const contextId = ref('');

const featuredVideos = computed(() =>
  (searchResults.value[contextId.value] || []).toSorted((b, a) => {
    if (!collection.value || collection.value.contents.type !== 'ContentsByReferences') {
      return 0;
    }
    const videoIdsInOrder = collection.value.contents.references.map((ref) => ref.id);
    return videoIdsInOrder.indexOf(b.id) - videoIdsInOrder.indexOf(a.id);
  }),
);
watchEffect(async () => {
  if (collection.value?.contents.type !== 'ContentsByReferences') {
    return;
  }
  const searchQuery: SearchQuery = {
    filter: { id: collection.value.contents.references.map((reference) => reference.id) },
    pagination: { size: 4 },
  };
  contextId.value = videoStore.search(searchQuery);
});

const { categories } = useCategories();
const { t, l } = useLocale();
const categoryInCurrentRealm = (categoryIds: string[]) =>
  categoryIds.find((categoryId) =>
    categories.value.find((category) => category.kind === 'subject' && category.id === categoryId),
  );
</script>
<template>
  <v-sheet class="wrapper pa-4 px-sm-8 py-sm-6 mb-6">
    <v-row class="subject-list-row">
      <v-col>
        <h2 class="subject-list-row--heading text-secondary ma-0 mb-3">{{ t(l.subject.latest_films) }}</h2>
        <v-row>
          <template v-if="!featuredVideos.length">
            <v-col v-for="n in 4" :key="n" cols="6" sm="4" md="3">
              <v-skeleton-loader class="overflow-hidden" type="card" max-height="120"></v-skeleton-loader>
            </v-col>
          </template>
          <VideoThumbnail
            v-for="(vid, index) in featuredVideos"
            v-else
            :key="index"
            :subject-id="categoryInCurrentRealm(vid.categories) || ''"
            :video="vid"
            :use-theme-styling="false"
          />
          <!-- TODO: handle multisubject videos, see https://iltservices.atlassian.net/browse/VID-1086 -->
        </v-row>
      </v-col>
    </v-row></v-sheet
  >
</template>
<style lang="scss" scoped>
@use '@/foundations/theme/settings';
@use 'sass:map';

.subject-list-row {
  &--heading {
    display: inline-block;
    font-size: 1.125rem;
    margin: 0 0 5px;

    @media #{map.get(settings.$display-breakpoints, 'md-and-up')} {
      font-size: 1.375rem;
      vertical-align: text-bottom;
    }
  }
}

.wrapper {
  border-left: 10px solid rgba(var(--v-theme-outline-variant));
  position: relative;
}
</style>
